import { useRef, useState } from "react";

import AppButton from "../../../commons/buttons/AppButton";
import AppContainer from "../../../commons/containers/AppContainer";
import AppLinkButton from "../../../commons/buttons/AppLinkButton";
import CustomerAPI from "../CustomerAPI";
import HallAppToast from "../../../commons/HallAppToast";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

const service = new CustomerAPI();

function ImportCustomerModal({ closeShow, show }) {
  const navigate = useNavigate();

  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // ✅ novo estado: após sucesso
  const [importQueued, setImportQueued] = useState(false);

  const handleDownload = () => {
    window.open(
      `${process.env.PUBLIC_URL}/planilhas/planilha_modelo_clientes.csv`,
      "_blank"
    );
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = [".csv", ".xlsx", ".xls"];
    const ok = allowed.some((ext) => file.name.toLowerCase().endsWith(ext));
    if (!ok) {
      HallAppToast.error("Selecione um arquivo .csv, .xlsx ou .xls");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      HallAppToast.error("Arquivo muito grande (máximo 5MB)");
      e.target.value = "";
      return;
    }

    setSelectedFile(file);
  };

  const goToCustomers = () => {
    closeShow?.();
    // ajuste para sua rota real
    navigate("/clientes");
  };

  const uploadFile = async () => {
    if (!selectedFile) {
      HallAppToast.error("Selecione uma planilha primeiro.");
      return;
    }
    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", selectedFile);

      await service.importCustomers(formData);

      // ✅ troca tela para “processando em paralelo”
      setImportQueued(true);

      // opcional: limpar seleção para evitar reenvio acidental
      setSelectedFile(null);

      // toast opcional (pode manter ou remover)
      HallAppToast.success("Planilha recebida! Vamos processar em segundo plano.");
    } catch (err) {
      HallAppToast.error("Erro ao importar planilha. Tente novamente.");
      console.error(err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Modal
      className="hallapp-import-modal"
      show={show}
      fullscreen
      onHide={closeShow}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ textAlign: "center", fontSize: "15px" }}>
          IMPORTAR CLIENTE
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4 p-md-5">
        <AppContainer>
          {/* ✅ tela pós-envio */}
          {importQueued ? (
            <div className="text-center">
              <h5 className="mb-3" style={{ fontWeight: 700 }}>
                Recebemos sua planilha ✅
              </h5>

              <p className="text-muted fs-6" style={{ maxWidth: 720, margin: "0 auto" }}>
                O processamento será feito em paralelo (em segundo plano).
                <br />
                Volte na tela de <b>Clientes</b> em até <b>5 minutos</b> para ver os registros importados.
              </p>

              <p className="text-muted mt-3" style={{ maxWidth: 720, margin: "0 auto" }}>
                Se após 5 minutos os clientes não aparecerem, entre em contato com o suporte para verificarmos.
              </p>

              <div className="d-flex justify-content-center gap-2 mt-4 flex-wrap">
                <AppButton
                  name="Voltar para Clientes"
                  onClick={goToCustomers}
                  style={{
                    background: "linear-gradient(90deg, #7A2DF2, #5E17EB)",
                    border: "none",
                    color: "#fff",
                    fontWeight: "600",
                    fontSize: "15px",
                    padding: "10px 20px",
                    borderRadius: "12px",
                    boxShadow: "0 6px 18px rgba(94, 23, 235, 0.25)",
                  }}
                />

                <AppLinkButton
                  name="Fechar"
                  onClick={closeShow}
                />
              </div>
            </div>
          ) : (
            <>
              {/* tela normal */}
              <div className="text-center mb-4">
                <p className="text-muted fs-6">
                  Para importar sua base de clientes, baixe o modelo abaixo, preencha com
                  as informações necessárias e envie de volta pelo sistema.
                </p>
              </div>

              <div className="d-flex justify-content-center mb-4">
                <AppButton
                  name="Baixar Planilha Modelo"
                  width="auto"
                  onClick={handleDownload}
                  style={{
                    background: "linear-gradient(90deg, #7A2DF2, #5E17EB)",
                    border: "none",
                    color: "#fff",
                    fontWeight: "600",
                    fontSize: "15px",
                    padding: "12px 24px",
                    borderRadius: "12px",
                    boxShadow: "0 6px 18px rgba(94, 23, 235, 0.3)",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                />
              </div>

              <hr />

              <div className="text-center">
                <div className="d-flex justify-content-center">
                  <AppLinkButton
                    name="Importar planilha"
                    onClick={openFilePicker}
                    disabled={uploading}
                  />
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                />

                <div className="mt-2 small text-muted" style={{ minHeight: 20 }}>
                  {selectedFile
                    ? `Selecionado: ${selectedFile.name}`
                    : "Nenhum arquivo selecionado"}
                </div>

                {selectedFile && (
                  <div className="d-flex justify-content-center mt-3">
                    <AppButton
                      name={uploading ? "Enviando..." : "Enviar planilha"}
                      onClick={uploadFile}
                      disabled={uploading}
                      style={{
                        background: "linear-gradient(90deg, #7A2DF2, #5E17EB)",
                        border: "none",
                        color: "#fff",
                        fontWeight: "600",
                        fontSize: "15px",
                        padding: "10px 20px",
                        borderRadius: "12px",
                        boxShadow: "0 6px 18px rgba(94, 23, 235, 0.25)",
                      }}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </AppContainer>
      </Modal.Body>
    </Modal>
  );
}

export default ImportCustomerModal;
